import type {
  LearnedAutoFillMergeItem,
  LearnedAutoFillMergeStatus,
  LearnedAutoFillPatternV1,
} from './learnedAutoFillContract';
import {
  createLearnedPatternSignature,
  getLearnedRequiredKeysValidationError,
  hasLearnedAutoFillMappingValue,
} from './learnedAutoFillPatterns';

function patternsEqual(
  left: LearnedAutoFillPatternV1,
  right: LearnedAutoFillPatternV1
): boolean {
  return JSON.stringify(left.values) === JSON.stringify(right.values) &&
    createLearnedPatternSignature(left.requiredKeys) === createLearnedPatternSignature(right.requiredKeys);
}

export function planLearnedAutoFillMerge(
  existing: readonly LearnedAutoFillPatternV1[],
  incoming: readonly LearnedAutoFillPatternV1[]
): LearnedAutoFillMergeItem[] {
  const known = new Map(existing.map((entry) => [entry.id, entry]));

  return incoming.map((entry) => {
    const current = known.get(entry.id);
    const status: LearnedAutoFillMergeStatus = !current
      ? 'new'
      : patternsEqual(current, entry) ? 'unchanged' : 'conflict';
    if (status === 'new') {
      known.set(entry.id, entry);
    }
    return { incoming: entry, existing: current, status };
  });
}

export function applyLearnedAutoFillMerge(
  existing: readonly LearnedAutoFillPatternV1[],
  plan: readonly LearnedAutoFillMergeItem[],
  confirmedConflictIds: ReadonlySet<string>
): LearnedAutoFillPatternV1[] {
  const merged = new Map(existing.map((entry) => [entry.id, entry]));

  plan.forEach((item) => {
    if (item.status === 'new' ||
      (item.status === 'conflict' && confirmedConflictIds.has(item.incoming.id))) {
      merged.set(item.incoming.id, item.incoming);
    }
  });

  return Array.from(merged.values()).sort((left, right) => left.id.localeCompare(right.id));
}

export function isLearnedAutoFillMergeItemActionable(
  item: LearnedAutoFillMergeItem,
  confirmedConflictIds: ReadonlySet<string>
): boolean {
  const hasRequiredValues = Boolean(item.incoming.sourceFamilyName.trim()) &&
    hasLearnedAutoFillMappingValue(item.incoming.values);
  return hasRequiredValues &&
    !getLearnedRequiredKeysValidationError(item.incoming.requiredKeys) && (
    item.status === 'new' ||
    (item.status === 'conflict' && confirmedConflictIds.has(item.incoming.id))
  );
}
